import { requestWithPromise } from '..'
import { HttpMethods } from '../apiHelper'
import apiClient from './apiClient'

/**
 * Pre-login routing: tells whether the account behind that email logs in through its institution
 * SSO ({ personalSso: true, institutionId }) or through the local password login
 * ({ personalSso: false }). An unknown account also answers personalSso: false (this prevents
 * account enumeration).
 */
export const personnalSso = (email) => {
  return apiClient.get(null, `auth/personnal-sso?email=${encodeURIComponent(email)}`)
}

/**
 * Local login (account password). Without a profileId, the backend answers either
 * { token, accountId, profileId } (single profile) or { accountId, profiles }: the caller then
 * shows the profile selector and calls back with the chosen profile (two-step login, credentials
 * sent again because the route is stateless).
 * Errors (401 invalid credentials, 403 PROFILE_NOT_LINKED_TO_ACCOUNT) are propagated to the
 * caller instead of going through the global error display.
 */
export const login = async (email, password, profileId = null) => {
  const body = { email, password }

  if (profileId !== null) {
    body['profile-id'] = profileId
  }

  return await requestWithPromise('/auth', HttpMethods.POST, body, null, true, true)
}

/**
 * SSO login (external accounts only): exchanges the PKCE code for an internal JWT.
 * Same two-step response contract as the local login.
 */
export const loginSSO = async (authorizationCode, codeVerifier, profileId = null) => {
  const body = { code: authorizationCode, codeVerifier: codeVerifier }

  if (profileId !== null) {
    body['profile-id'] = profileId
  }

  return await requestWithPromise('/auth/login-sso', HttpMethods.POST, body)
}

/**
 * Switches the active profile of the account: the backend checks that the target profile does
 * belong to the current user's account and returns a new token bound to that profile
 * ({ token, accountId, profileId }).
 */
export const switchProfile = async (user, profileId) => {
  return await requestWithPromise(
    '/auth/switch-profile',
    HttpMethods.POST,
    { 'profile-id': profileId },
    { token: user.token },
    false,
    true
  )
}

/**
 * Sliding renewal: reissues a JWT for the current profile as long as the current token is still
 * valid (the backend token expires after 24 h). Called periodically so that a session kept open
 * for more than 24 h does not suddenly start answering 401. The global error handler is disabled
 * (a background reissue must not raise a notification) and the error is propagated to the caller,
 * which silently ignores it (a real request will handle the actual expiry).
 */
export const refresh = async (user) => {
  return await requestWithPromise(
    '/auth/refresh',
    HttpMethods.POST,
    {},
    { token: user.token },
    true,
    true
  )
}

/**
 * Public "forgot password" entry point: emails a link to pick a new password (valid 24 h, or the
 * account creation email again when the account never set a password). Called from "My profile",
 * where a user asks the reset of its own account password.
 *
 * Always answers 200 — an unknown, deleted or external-SSO account, and a reset asked again too
 * soon, are silently ignored so the route cannot be used to enumerate accounts. The caller can
 * therefore never tell whether an email was really sent.
 */
export const askPasswordReset = async (email) => {
  return await requestWithPromise('/auth/reset-password', HttpMethods.POST, { email }, null, true, true)
}
